import { useState, useEffect, useMemo, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { DateRange } from 'react-day-picker';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { Separator } from '@/components/ui/separator';
import { Heart, Shield, MapPin, Bell, AlertTriangle, Thermometer, Pill, Clock, CalendarIcon, Search, Check, User, Utensils, Sun, Phone, Building2, Ambulance, MessageSquare, Save, Star, Trash2, BarChart3, X, BookOpen, Lightbulb, Plus, Award, Lock, Crown } from 'lucide-react';
import { getDestinationHealthInfo } from '@/utils/dynamicHealthTips';
import { getTimezoneForDestination, getTimezonesList } from '@/utils/timezoneUtils';
import { getEmergencyResources } from '@/utils/emergencyResources';
import { LOCATIONS } from '@/components/ui/location-autocomplete';
import { RecentSearches } from '@/components/ui/recent-searches';
import { useRecentSearches } from '@/hooks/useRecentSearches';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useSubscription } from '@/hooks/useSubscription';
import { useFeatureUsage } from '@/hooks/useFeatureUsage';
import { useNavigate } from 'react-router-dom';
import jetLagBg from '@/assets/jet-lag-bg.png';

// Using comprehensive LOCATIONS database with 2000+ destinations

interface JetLagPlan {
  currentTimezone: string;
  destinationTimezone: string;
  flightDuration: number;
  timeDifference: number;
  departureTime: string;
  arrivalTime: string;
  estimatedArrival: string;
}
interface VaccineInfo {
  name: string;
  type: 'mandatory' | 'recommended' | 'routine';
  timing: string; // When to get it before travel
  description?: string;
}
interface HealthRisk {
  name: string;
  severity: 'low' | 'medium' | 'high';
  description: string;
  prevention: string;
}
interface SeasonalHealthInfo {
  season: string;
  risks: string[];
  precautions: string[];
  monthlyRisks: {
    [month: string]: string[];
  };
}
interface HealthAlert {
  type: 'outbreak' | 'seasonal' | 'air-quality' | 'travel-advisory';
  severity: 'low' | 'medium' | 'high';
  message: string;
  source?: string;
  date?: string;
}
interface EmergencyContact {
  service: string;
  number: string;
  available: string;
}
interface MedicalFacility {
  name: string;
  type: 'hospital' | 'clinic' | 'pharmacy';
  address: string;
  phone?: string;
  hours?: string;
}
interface EmergencyPhrase {
  english: string;
  local: string;
  pronunciation?: string;
}
interface EmergencyResources {
  emergencyNumbers: EmergencyContact[];
  medicalFacilities: MedicalFacility[];
  travelHotlines: EmergencyContact[];
  keyPhrases: EmergencyPhrase[];
}
interface DestinationInfo {
  name: string;
  vaccinations: VaccineInfo[];
  healthRisks: (string | HealthRisk)[]; // Support both formats
  pharmacyItems: string[];
  nutritionTips: string[];
  currentAlerts: HealthAlert[];
  seasonalInfo?: SeasonalHealthInfo;
  emergencyResources?: EmergencyResources;
}
export function HealthWellness() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const {
    addRecentSearch
  } = useRecentSearches();
  const {
    user
  } = useAuth();
  const {
    toast
  } = useToast();
  const { hasFeature, subscription } = useSubscription();
  const { recordUsage } = useFeatureUsage();
  
  // Check if user has access to health tracking features (premium_economy or first_class)
  const canUseHealthHub = hasFeature('health_tracking');
  const [jetLagUsageRecorded, setJetLagUsageRecorded] = useState(false);
  
  const [destination, setDestination] = useState('');
  const [destinationInput, setDestinationInput] = useState('');
  const [showDestinationSuggestions, setShowDestinationSuggestions] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: new Date(),
    to: undefined
  });
  const [jetLagPlan, setJetLagPlan] = useState<JetLagPlan>({
    currentTimezone: '',
    destinationTimezone: '',
    flightDuration: 0,
    timeDifference: 0,
    departureTime: '',
    arrivalTime: '',
    estimatedArrival: ''
  });
  const [showJetLagPlan, setShowJetLagPlan] = useState(false);
  const [destinationInfo, setDestinationInfo] = useState<DestinationInfo | null>(null);
  const [loadingAlerts, setLoadingAlerts] = useState(false);
  const [showVaccinationDetails, setShowVaccinationDetails] = useState(false);
  const [savedPlans, setSavedPlans] = useState<any[]>([]);
  const [showSavedPlans, setShowSavedPlans] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  const [comparisonPlans, setComparisonPlans] = useState<any[]>([]);
  const [savingPlan, setSavingPlan] = useState(false);
  const [showFlightComparison, setShowFlightComparison] = useState(false);
  const [flightOptions, setFlightOptions] = useState<Array<{
    id: string;
    name: string;
    departureTime: string;
    arrivalTime: string;
    flightDuration: number;
  }>>([]);

  // Auto-detect user's current timezone on mount and load saved plans
  useEffect(() => {
    try {
      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const now = new Date();
      const offsetMinutes = -now.getTimezoneOffset();
      const offsetHours = offsetMinutes / 60;

      // Format offset as UTC string (e.g., "UTC+5:30" or "UTC-8")
      const offsetString = offsetHours >= 0 ? `UTC+${Math.floor(offsetHours)}${offsetMinutes % 60 !== 0 ? ':' + Math.abs(offsetMinutes % 60).toString().padStart(2, '0') : ''}` : `UTC${Math.floor(offsetHours)}${offsetMinutes % 60 !== 0 ? ':' + Math.abs(offsetMinutes % 60).toString().padStart(2, '0') : ''}`;

      // Find matching timezone from list or create custom one
      const timezonesList = getTimezonesList();
      const matchingTz = timezonesList.find(tz => tz.startsWith(offsetString));
      const detectedTz = matchingTz || `${offsetString} (${userTimeZone})`;
      setJetLagPlan(prev => ({
        ...prev,
        currentTimezone: detectedTz
      }));
    } catch (error) {
      console.error('Error detecting timezone:', error);
    }

    // Load saved jet lag plans
    if (user) {
      loadSavedPlans();
    }
  }, [user]);
  const loadSavedPlans = async () => {
    if (!user) return;
    try {
      const {
        data,
        error
      } = await supabase.from('jet_lag_plans').select('*').eq('user_id', user.id).order('created_at', {
        ascending: false
      });
      if (error) throw error;
      setSavedPlans(data || []);
    } catch (error) {
      console.error('Error loading saved plans:', error);
    }
  };
  const savePlan = async () => {
    if (!user || !jetLagPlan.currentTimezone || !jetLagPlan.destinationTimezone) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields before saving",
        variant: "destructive"
      });
      return;
    }
    const planName = prompt('Enter a name for this plan (e.g., "Morning Flight to Tokyo")');
    if (!planName) return;
    setSavingPlan(true);
    try {
      const severity = getJetLagSeverity();
      const recoveryDays = Math.ceil(Math.abs(jetLagPlan.timeDifference) * (jetLagPlan.timeDifference > 0 ? 1 : 0.67));
      const {
        error
      } = await supabase.from('jet_lag_plans').insert({
        user_id: user.id,
        plan_name: planName,
        current_timezone: jetLagPlan.currentTimezone,
        destination_timezone: jetLagPlan.destinationTimezone,
        destination_name: destination || null,
        flight_duration: jetLagPlan.flightDuration,
        time_difference: jetLagPlan.timeDifference,
        estimated_arrival: jetLagPlan.estimatedArrival || null,
        departure_time: jetLagPlan.departureTime || null,
        jet_lag_severity: severity.level,
        recovery_days: recoveryDays
      });
      if (error) throw error;
      toast({
        title: "Plan Saved!",
        description: `"${planName}" has been saved successfully`
      });
      await loadSavedPlans();
    } catch (error) {
      console.error('Error saving plan:', error);
      toast({
        title: "Save Failed",
        description: "Could not save the plan. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSavingPlan(false);
    }
  };
  const loadPlan = (plan: any) => {
    setJetLagPlan({
      currentTimezone: plan.current_timezone,
      destinationTimezone: plan.destination_timezone,
      flightDuration: parseFloat(plan.flight_duration),
      timeDifference: parseFloat(plan.time_difference),
      departureTime: plan.departure_time || '',
      arrivalTime: '',
      estimatedArrival: plan.estimated_arrival || ''
    });
    if (plan.destination_name) {
      setDestination(plan.destination_name);
      setDestinationInput(plan.destination_name);
    }
    setShowSavedPlans(false);
    toast({
      title: "Plan Loaded",
      description: `"${plan.plan_name}" has been loaded`
    });
  };
  const deletePlan = async (planId: string, planName: string) => {
    if (!confirm(`Delete "${planName}"?`)) return;
    try {
      const {
        error
      } = await supabase.from('jet_lag_plans').delete().eq('id', planId);
      if (error) throw error;
      toast({
        title: "Plan Deleted",
        description: `"${planName}" has been removed`
      });
      await loadSavedPlans();
    } catch (error) {
      console.error('Error deleting plan:', error);
      toast({
        title: "Delete Failed",
        description: "Could not delete the plan",
        variant: "destructive"
      });
    }
  };
  const toggleFavorite = async (planId: string, currentFavorite: boolean) => {
    try {
      const {
        error
      } = await supabase.from('jet_lag_plans').update({
        is_favorite: !currentFavorite
      }).eq('id', planId);
      if (error) throw error;
      await loadSavedPlans();
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };
  const addToComparison = (plan: any) => {
    if (comparisonPlans.find(p => p.id === plan.id)) {
      setComparisonPlans(comparisonPlans.filter(p => p.id !== plan.id));
    } else if (comparisonPlans.length < 3) {
      setComparisonPlans([...comparisonPlans, plan]);
    } else {
      toast({
        title: "Comparison Limit",
        description: "You can compare up to 3 plans at once",
        variant: "destructive"
      });
    }
  };

  // Filter destinations based on input
  const filteredDestinations = useMemo(() => {
    if (!destinationInput.trim()) return [];
    const searchTerm = destinationInput.toLowerCase().trim();
    return LOCATIONS.filter(destination => destination.name.toLowerCase().includes(searchTerm) || destination.country.toLowerCase().includes(searchTerm)).sort((a, b) => a.name.localeCompare(b.name)).slice(0, 8);
  }, [destinationInput]);
  const selectDestination = useCallback((dest: {
    name: string;
    country: string;
  }) => {
    const fullDestination = `${dest.name}, ${dest.country}`;
    setDestination(fullDestination);
    setDestinationInput(fullDestination);
    setShowDestinationSuggestions(false);
    addRecentSearch(dest.name, dest.country);
    
    // Auto-populate destination timezone based on selected destination
    const timezoneInfo = getTimezoneForDestination(fullDestination);
    if (timezoneInfo) {
      setJetLagPlan(prev => ({
        ...prev,
        destinationTimezone: timezoneInfo.label,
        timeDifference: calculateTimeDifference(prev.currentTimezone, timezoneInfo.label)
      }));
    }
    
    console.log('Selected destination:', fullDestination, 'Timezone:', timezoneInfo?.label);
  }, [addRecentSearch]);
  const handleDestinationChange = useCallback((value: string) => {
    setDestinationInput(value);
    setShowDestinationSuggestions(value.trim().length > 0);
    // Don't update destination state while typing, only when selecting from dropdown
  }, []);

  // Fetch real-time health alerts and destination data
  useEffect(() => {
    if (destination && destination.length > 3) {
      // Only fetch if destination is a complete location (more than 3 chars and likely selected from dropdown)
      fetchDestinationHealthData(destination, dateRange?.from, dateRange?.to);
    } else {
      setDestinationInfo(null);
    }
  }, [destination, dateRange]);
  const fetchDestinationHealthData = async (dest: string, fromDate?: Date, toDate?: Date) => {
    setLoadingAlerts(true);
    console.log('Fetching health data for destination:', dest); // Debug log
    try {
      // Fetch comprehensive live health data including vaccinations, alerts, risks, pharmacy items, and nutrition tips
      const vaccinationResponse = await supabase.functions.invoke('vaccination-requirements', {
        body: {
          destination: dest,
          travelDate: fromDate?.toISOString() || new Date().toISOString()
        }
      });
      if (vaccinationResponse.data) {
        // Base live data mapping from edge function
        const baseHealthRisks: string[] = (vaccinationResponse.data.healthRisks || []).map((risk: any) => `${risk.name}: ${risk.description}${risk.prevention ? ` (Prevention: ${risk.prevention})` : ''}`);
        let liveData: DestinationInfo = {
          name: dest,
          vaccinations: vaccinationResponse.data.vaccinations || [],
          healthRisks: baseHealthRisks,
          pharmacyItems: vaccinationResponse.data.pharmacyItems || [],
          nutritionTips: vaccinationResponse.data.nutritionTips || [],
          currentAlerts: vaccinationResponse.data.healthAlerts || []
        };

        // Try to fetch additional seasonal health info and enrich risks when empty
        try {
          const alertsResponse = await supabase.functions.invoke('health-alerts', {
            body: {
              location: dest,
              fromDate: fromDate?.toISOString(),
              toDate: toDate?.toISOString()
            }
          });
          const seasonalInfo = alertsResponse.data?.seasonalInfo;
          const alerts = alertsResponse.data?.alerts || [];

          // If no risks were returned by vaccinations function, derive from seasonal risks and alerts
          if (liveData.healthRisks.length === 0) {
            const seasonalRisks: string[] = Array.isArray(seasonalInfo?.risks) ? seasonalInfo.risks : [];
            const alertMessages: string[] = alerts.map((a: any) => a?.message).filter(Boolean);
            const combined = Array.from(new Set([...seasonalRisks, ...alertMessages].filter(Boolean)));
            if (combined.length > 0) {
              liveData = {
                ...liveData,
                healthRisks: combined
              };
            } else {
              // Final fallback: use static generator for destination-specific risks
              try {
                const fallback = await getDestinationHealthInfo(dest);
                if (fallback.healthRisks?.length) {
                  liveData = {
                    ...liveData,
                    name: dest,
                    healthRisks: fallback.healthRisks
                  };
                }
              } catch {}
            }
          }
          if (seasonalInfo) {
            setDestinationInfo({
              ...liveData,
              seasonalInfo,
              emergencyResources: getEmergencyResources(dest)
            });
          } else {
            setDestinationInfo({
              ...liveData,
              emergencyResources: getEmergencyResources(dest)
            });
          }
        } catch {
          setDestinationInfo({
            ...liveData,
            emergencyResources: getEmergencyResources(dest)
          });
        }
      } else {
        // Fallback to static data if API fails
        const fallbackData = await getDestinationHealthInfo(dest);
        setDestinationInfo({
          ...fallbackData,
          name: dest,
          emergencyResources: getEmergencyResources(dest)
        });
      }
    } catch (error) {
      console.error('Error fetching health data:', error);
      // Fallback to static data
      const fallbackData = await getDestinationHealthInfo(dest);
      setDestinationInfo({
        ...fallbackData,
        name: dest,
        emergencyResources: getEmergencyResources(dest)
      });
    } finally {
      setLoadingAlerts(false);
    }
  };
  const getDestinationHealthData = async (dest: string, travelDate?: Date): Promise<DestinationInfo> => {
    const healthInfo = await getDestinationHealthInfo(dest);
    const emergencyResources = getEmergencyResources(dest);
    return {
      ...healthInfo,
      emergencyResources
    };
  };
  const getJetLagAdvice = () => {
    const {
      timeDifference,
      flightDuration,
      estimatedArrival
    } = jetLagPlan;
    if (!timeDifference) return [];
    const isEastward = timeDifference > 0;
    const hours = Math.abs(timeDifference);

    // Parse arrival time to get hour for personalized advice
    const arrivalHour = estimatedArrival ? parseInt(estimatedArrival.split(':')[0]) : null;
    const getArrivalPeriod = () => {
      if (!arrivalHour) return 'unknown';
      if (arrivalHour >= 5 && arrivalHour < 12) return 'morning';
      if (arrivalHour >= 12 && arrivalHour < 17) return 'afternoon';
      if (arrivalHour >= 17 && arrivalHour < 22) return 'evening';
      return 'night';
    };
    const arrivalPeriod = getArrivalPeriod();
    const isPM = arrivalHour && arrivalHour >= 12;
    const advice = [];

    // Pre-travel preparation - scientifically accurate circadian rhythm shifting
    if (hours >= 3) {
      advice.push({
        phase: "Before Travel (3-4 days prior)",
        tips: [
          isEastward 
            ? "Gradually shift your sleep schedule earlier by 30-60 minutes each night. This pre-adaptation helps your body start adjusting to the new timezone before departure." 
            : "Gradually shift your sleep schedule later by 30-60 minutes each night. For westward travel, staying up later is easier than waking earlier.",
          "Begin shifting meal times toward your destination's schedule. Eating at destination meal times helps reset your circadian rhythm faster.",
          isEastward 
            ? "Get bright morning light exposure (6-10 AM) to advance your internal clock. Open curtains immediately upon waking and spend time outdoors." 
            : "Seek bright light in the evening (6-9 PM) to delay your internal clock. Avoid bright morning light during pre-adjustment.",
          hours >= 6 ? "Consider booking a strategic stopover to break up extreme time zone changes into more manageable segments." : "",
          "Avoid alcohol and caffeine in the 48 hours before departure to ensure your sleep quality remains high during the adjustment period."
        ].filter(Boolean)
      });
    }

    // During flight - personalized based on arrival time
    const flightTips = [
      "Immediately set your watch and all devices to destination time. This mental shift is crucial for adapting quickly.",
      "Stay well-hydrated throughout the flight (8oz water per hour). Cabin air is extremely dry, and dehydration worsens jet lag symptoms.",
      "Avoid alcohol entirely - it disrupts sleep quality and increases dehydration, making jet lag recovery significantly harder.",
      "Limit caffeine to strategic times only. If you need to stay awake, use it sparingly and stop 6 hours before planned sleep."
    ];
    
    if (estimatedArrival && flightDuration >= 6) {
      switch (arrivalPeriod) {
        case 'morning':
          flightTips.unshift(
            "Sleep during the second half of your flight so you wake refreshed near landing time. Use earplugs, eye mask, and recline fully.",
            "Avoid screens 90 minutes before planned sleep. Blue light suppresses melatonin and makes falling asleep harder at altitude."
          );
          break;
        case 'afternoon':
          flightTips.unshift(
            "Sleep during the first half of the flight, then wake 3-4 hours before landing. This ensures you're alert upon arrival.",
            "Use the in-flight entertainment during the wake period to keep yourself engaged and prevent falling back asleep."
          );
          break;
        case 'evening':
          flightTips.unshift(
            "Stay awake during the entire flight if possible. Arriving tired helps you fall asleep at the proper local bedtime.",
            "If you must nap, limit it to 20 minutes maximum. Set an alarm and stay upright to avoid deep sleep."
          );
          break;
        case 'night':
          flightTips.unshift(
            "Get some sleep on the plane, but wake up 2-3 hours before landing to start adjusting to nighttime arrival.",
            "Keep the cabin lights dim after waking and avoid bright screens to maintain melatonin production for later sleep."
          );
          break;
        default:
          flightTips.unshift("Strategically time your sleep based on your arrival time. Aim to match the sleep-wake cycle you'll need upon landing.");
      }
    } else {
      flightTips.unshift("For shorter flights, staying awake is often better than trying to sleep for just 1-2 hours.");
    }
    
    advice.push({
      phase: "During Flight",
      tips: flightTips
    });

    // Upon arrival - highly personalized based on arrival time
    const arrivalTips = [];
    if (estimatedArrival) {
      switch (arrivalPeriod) {
        case 'morning':
          arrivalTips.push(
            "Get immediate bright sunlight exposure outdoors. Morning light is the most powerful circadian signal - aim for 30-60 minutes within the first 2 hours of arrival.",
            "Eat a substantial protein-rich breakfast immediately. This signals to your body that it's the start of the active day.",
            "Stay physically active throughout the day. Walk, explore, and keep moving to fight fatigue naturally without napping.",
            "Absolutely no napping, no matter how tired you feel. Push through until local bedtime for fastest adjustment."
          );
          break;
        case 'afternoon':
          arrivalTips.push(
            "Get moderate sunlight exposure, but avoid intense midday sun if you're arriving from a much earlier timezone.",
            "Have a proper lunch at local lunchtime, even if your body says it's dinner or breakfast time. Meal timing is a powerful circadian cue.",
            "Conserve your energy for the evening. You need to stay awake until at least 9-10 PM local time to reset properly.",
            "If extremely fatigued, take a 20-minute power nap maximum, but only before 3 PM local time."
          );
          break;
        case 'evening':
          arrivalTips.push(
            "Avoid bright lights as much as possible. Dim indoor lighting helps signal to your body that bedtime is approaching.",
            "Have a light dinner at normal local dinner time. Don't skip it even if you're not hungry - eating at local times helps reset your clock.",
            "This is optimal arrival timing - you can go to sleep at a normal local bedtime (9-11 PM) without forcing yourself to stay awake or sleep.",
            "Keep your room cool (65-68°F) and very dark to optimize sleep quality on your first night."
          );
          break;
        case 'night':
          arrivalTips.push(
            "Use only dim red lights if you must move around. Avoid all bright lights and especially blue light from screens.",
            "Go to sleep immediately upon reaching your accommodation. Your circadian rhythm is naturally ready for sleep.",
            "Set an alarm to wake at a normal local morning time (7-9 AM), even if you haven't gotten enough sleep. Consistency is crucial.",
            "Keep your room completely dark and cool. Use blackout curtains or an eye mask to ensure no light exposure during sleep."
          );
          break;
        default:
          if (!isPM) {
            arrivalTips.push("Get sunlight exposure to reset your circadian rhythm, but timing matters based on your specific arrival time.");
          }
          arrivalTips.push("Eat at local meal times immediately, even if your appetite doesn't match. This is one of the strongest circadian signals.");
      }
    } else {
      arrivalTips.push(
        "Get bright sunlight exposure at the appropriate time for your arrival. Morning arrivals need immediate sun; evening arrivals should avoid it.",
        "Immediately adopt local meal times, even if you're not hungry. Your digestive system is a powerful clock-resetting mechanism."
      );
    }
    
    if (arrivalPeriod !== 'night') {
      arrivalTips.push("Stay awake until at least 9-10 PM local time, no matter how exhausted you feel. This single action determines how quickly you'll adjust.");
    }
    
    if (hours >= 6) {
      arrivalTips.push("Consider taking 0.5-3mg of melatonin 30 minutes before your target bedtime. Start with the lowest dose. It helps initiate sleep but won't keep you asleep.");
    }
    
    arrivalTips.push("Avoid alcohol on your first night - it fragments sleep and prevents deep rest, which you critically need for recovery.");
    
    advice.push({
      phase: `Upon Arrival${estimatedArrival ? ` (${estimatedArrival})` : ''}`,
      tips: arrivalTips
    });

    // First 2-3 Days - Consolidation Phase
    const consolidationTips = [
      "Maintain strict consistency with meal times at the local schedule. Eat breakfast, lunch, and dinner at the same times each day - no skipping meals.",
      isEastward 
        ? "Get bright morning light exposure (6-10 AM) every single day. This morning light locks in your new schedule. Aim for at least 30 minutes outdoors." 
        : "Avoid bright morning light for the first few days. Get your light exposure in the afternoon and evening instead to help delay your rhythm.",
      "Continue avoiding daytime naps. If you must nap, keep it under 20 minutes and before 2 PM local time only.",
      "Exercise during local daytime hours, but avoid intense workouts within 3 hours of bedtime as they can interfere with sleep.",
      hours >= 5 
        ? "Be patient with yourself - full adjustment to time zone changes of 5+ hours typically takes 4-7 days. Your body is doing complex biological recalibration." 
        : `Full adjustment typically takes ${Math.ceil(hours * (isEastward ? 1 : 0.67))} days for your ${hours}-hour time difference. ${isEastward ? 'Eastward' : 'Westward'} travel adjusts at ${isEastward ? '1 day per hour' : 'roughly 1.5 hours per day'}.`,
      "Maintain a consistent sleep schedule - go to bed and wake up at the same time every day, even on weekends during your adjustment period.",
      "Stay hydrated and eat nutritious meals. Your body needs extra resources to handle the stress of circadian disruption."
    ];
    
    advice.push({
      phase: "Days 2-3 (Consolidation)",
      tips: consolidationTips
    });
    return advice;
  };
  const getJetLagSeverity = () => {
    const hours = Math.abs(jetLagPlan.timeDifference);
    if (hours <= 2) return {
      level: "Minimal",
      color: "text-accent",
      bg: "bg-accent/10"
    };
    if (hours <= 4) return {
      level: "Mild",
      color: "text-brand-yellow",
      bg: "bg-brand-yellow-light/20"
    };
    if (hours <= 8) return {
      level: "Moderate",
      color: "text-brand-yellow",
      bg: "bg-brand-yellow/20"
    };
    return {
      level: "Severe",
      color: "text-primary",
      bg: "bg-primary/10"
    };
  };
  // getTimezonesList is now imported from '@/utils/timezoneUtils'
  const calculateTimeDifference = (current: string, destination: string) => {
    const getCurrentOffset = (tz: string) => {
      const match = tz.match(/UTC([+-]?\d+(?:\.\d+)?)/);
      return match ? parseFloat(match[1]) : 0;
    };
    const currentOffset = getCurrentOffset(current);
    const destOffset = getCurrentOffset(destination);
    return destOffset - currentOffset;
  };
  const getAlertColor = (severity: 'low' | 'medium' | 'high') => {
    switch (severity) {
      case 'low':
        return 'border-yellow-200 bg-yellow-50';
      case 'medium':
        return 'border-orange-200 bg-orange-50';
      case 'high':
        return 'border-red-200 bg-red-50';
    }
  };
  return <>
      <Helmet>
        <title>Travel Health & Wellness Guide | Vaccination Requirements & Medical Safety | OdyVoy</title>
        <meta name="description" content="Get personalized travel health advice, vaccination requirements, health alerts, and medical safety information for your destination. Jet lag management, emergency contacts, and travel pharmacy essentials." />
        <meta name="keywords" content="travel health, travel vaccinations, travel medicine, health requirements, yellow fever, travel pharmacy, jet lag advice, travel safety, health alerts, emergency medical, travel wellness" />
        <link rel="canonical" href="https://odyvoy.com/health-wellness" />
        
        {/* Open Graph tags */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Travel Health & Wellness Guide - Vaccination Requirements & Medical Safety" />
        <meta property="og:description" content="Get personalized travel health advice, vaccination requirements, health alerts, and medical safety information for your destination worldwide." />
        <meta property="og:url" content="https://odyvoy.com/health-wellness" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Travel Health & Wellness Guide | OdyVoy" />
        <meta name="twitter:description" content="Personalized travel health advice, vaccination requirements, and medical safety information for travelers worldwide." />
        
        {/* Structured Data - Health and Wellness Service */}
        <script type="application/ld+json">
          {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "MedicalWebPage",
          "name": "Travel Health and Wellness Guide",
          "description": "Comprehensive travel health information including vaccination requirements, health alerts, medical safety, and wellness advice for international travelers.",
          "url": "https://odyvoy.com/health-wellness",
          "specialty": "Travel Medicine",
          "about": {
            "@type": "MedicalCondition",
            "name": "Travel Health and Safety"
          },
          "mainEntity": {
            "@type": "MedicalGuidelineContraindication",
            "name": "Travel Health Guidelines",
            "guidelineSubject": [{
              "@type": "MedicalCondition",
              "name": "Travel Vaccinations"
            }, {
              "@type": "MedicalCondition",
              "name": "Jet Lag Management"
            }, {
              "@type": "MedicalCondition",
              "name": "Travel Medical Emergency"
            }]
          },
          "lastReviewed": new Date().toISOString(),
          "reviewedBy": {
            "@type": "Organization",
            "name": "OdyVoy"
          }
        })}
        </script>

        {/* FAQ Structured Data for common travel health questions */}
        <script type="application/ld+json">
          {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [{
            "@type": "Question",
            "name": "What vaccinations do I need for international travel?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Vaccination requirements vary by destination. Common travel vaccines include Yellow Fever (mandatory for some countries), Hepatitis A and B, Typhoid, Japanese Encephalitis, and routine vaccines like Tetanus. Consult a travel medicine specialist 4-8 weeks before departure for personalized advice based on your destination, travel dates, and health history."
            }
          }, {
            "@type": "Question",
            "name": "How can I manage jet lag when traveling?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "To minimize jet lag: (1) Gradually adjust your sleep schedule 3-4 days before travel, (2) Stay hydrated and avoid alcohol during flights, (3) Get sunlight exposure at appropriate times based on your destination, (4) Sleep according to local time upon arrival, and (5) Consider melatonin supplements for flights crossing 6+ time zones (consult your doctor first)."
            }
          }, {
            "@type": "Question",
            "name": "When should I get travel vaccinations?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Book your travel clinic appointment 6-8 weeks before departure. Some vaccines require multiple doses over several weeks, and your body needs time to build immunity. Yellow Fever vaccination must be given at least 10 days before travel to be valid."
            }
          }, {
            "@type": "Question",
            "name": "What should I pack in my travel pharmacy kit?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Essential travel pharmacy items include: prescription medications (with extra supply), pain relievers, anti-diarrheal medication, antihistamines, motion sickness tablets, antibacterial ointment, bandages, insect repellent with DEET, sunscreen (SPF 30+), hand sanitizer, and any destination-specific items like altitude sickness medication or antimalarial drugs."
            }
          }]
        })}
        </script>
      </Helmet>

      <div className="w-full max-w-4xl mx-auto space-y-6 p-4">
        {/* Header & Destination Input - Personalized Health Hub */}
        <Card className="border-cyan-500/20 overflow-hidden">
          <div className="relative bg-gradient-to-br from-cyan-50 via-teal-50/50 to-transparent dark:from-cyan-950/30 dark:via-teal-950/20 dark:to-transparent border-b border-cyan-500/10">
            <div className="absolute -top-12 -right-12 h-40 w-40 rounded-full bg-gradient-to-br from-cyan-400/20 to-teal-500/20 blur-3xl pointer-events-none" aria-hidden="true" />
            <CardHeader className="relative">
              <CardTitle className="flex items-center gap-3 text-xl sm:text-2xl">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-400 to-teal-500 text-white shadow-md shadow-cyan-500/30">
                  <Heart className="h-5 w-5" aria-hidden="true" />
                </span>
                <span className="bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                  Personalized Health Hub
                </span>
                {!canUseHealthHub && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Lock className="h-4 w-4 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Upgrade to Premium Economy or First Class to unlock this feature</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </CardTitle>
              <CardDescription className="text-sm sm:text-base">
                Travel health guidance tailored to your destination
              </CardDescription>
            </CardHeader>
          </div>
        <CardContent>
          {!canUseHealthHub ? (
            <div className="flex flex-col items-center justify-center py-8 space-y-4 text-center">
              <div className="p-4 rounded-full bg-primary/10">
                <Crown className="h-8 w-8 text-primary" />
              </div>
              <div className="space-y-2">
                <h3 className="font-semibold text-lg">Unlock Personalized Health Hub</h3>
                <p className="text-muted-foreground max-w-md">
                  Get access to personalized health alerts, vaccination requirements, emergency resources, and more for your travel destinations.
                </p>
              </div>
              <Button 
                onClick={() => navigate('/pricing')}
                className="mt-2"
              >
                Upgrade to Premium
              </Button>
            </div>
          ) : (
          <div className="max-w-2xl mx-auto space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="destination" className="text-sm font-medium">
                  Travel destination
                </Label>
                <Popover open={showDestinationSuggestions && filteredDestinations.length > 0} onOpenChange={() => {}}>
                  <PopoverTrigger asChild>
                    <div className="relative mt-2">
                      <Input id="destination" value={destinationInput} onChange={e => handleDestinationChange(e.target.value)} onFocus={() => {
                        if (destinationInput.trim().length > 0) {
                          setShowDestinationSuggestions(true);
                        }
                      }} onBlur={e => {
                        setTimeout(() => {
                          const activeElement = document.activeElement;
                          if (!activeElement || !e.currentTarget.contains(activeElement)) {
                            setShowDestinationSuggestions(false);
                          }
                        }, 150);
                      }} placeholder="e.g., Bangkok, Thailand" className="pr-10" autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck="false" inputMode="text" />
                      <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4 pointer-events-none" />
                    </div>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0 z-50 bg-popover border shadow-md" align="start" style={{
                    width: 'var(--radix-popover-trigger-width)'
                  }} onOpenAutoFocus={e => e.preventDefault()} onCloseAutoFocus={e => e.preventDefault()}>
                    <Command className="w-full">
                      <CommandList>
                        {filteredDestinations.length > 0 ? <CommandGroup>
                            {filteredDestinations.map((destination, index) => <CommandItem key={`${destination.name}-${destination.country}-${index}`} value={`${destination.name}, ${destination.country}`} onSelect={() => selectDestination(destination)} className="flex items-center justify-between p-3 cursor-pointer hover:bg-accent/50 transition-colors">
                                <div className="flex items-center space-x-3">
                                  <div className="flex-shrink-0">
                                    <MapPin className="h-4 w-4 text-primary" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium text-sm truncate">
                                      {destination.name}
                                    </div>
                                    <div className="text-xs text-muted-foreground truncate">
                                      {destination.country}
                                    </div>
                                  </div>
                                </div>
                                {destination.name === destination.name && <Check className="h-3 w-3 text-primary" />}
                              </CommandItem>)}
                          </CommandGroup> : destinationInput.trim().length > 0 ? <CommandEmpty className="p-4 text-center text-sm text-muted-foreground">
                            No destinations found. Try a different search term.
                          </CommandEmpty> : null}
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label htmlFor="travel-dates" className="text-sm font-medium">
                  Travel dates
                </Label>
                <div className="flex gap-2 mt-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button id="travel-dates" variant="outline" className={cn("flex-1 justify-start text-left font-normal", !dateRange && "text-muted-foreground")}>
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dateRange?.from ? dateRange.to ? <>
                              {format(dateRange.from, "LLL dd, y")} - {format(dateRange.to, "LLL dd, y")}
                            </> : format(dateRange.from, "LLL dd, y") : <span>Pick travel dates</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className={cn("w-auto p-0", isMobile && "w-[320px]")} align="start">
                      <Calendar mode="range" defaultMonth={dateRange?.from || new Date()} selected={dateRange} onSelect={setDateRange} numberOfMonths={isMobile ? 1 : 2} disabled={date => date < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus className={cn("p-3 pointer-events-auto", isMobile && "w-full")} />
                    </PopoverContent>
                  </Popover>
                  {dateRange && <Button variant="ghost" size="icon" onClick={() => setDateRange(undefined)} className="flex-shrink-0" title="Clear dates">
                      <X className="h-4 w-4" />
                    </Button>}
                </div>
              </div>
            </div>
            
            {/* Recently Searched Section */}
            <div className="mt-6">
              <RecentSearches onSelect={(dest, country) => {
                selectDestination({
                  name: dest,
                  country
                });
              }} />
            </div>
          </div>
          )}
        </CardContent>
      </Card>

      {/* Destination-Specific Information */}
      {canUseHealthHub && destinationInfo && <div className="space-y-4">
          
          {/* Health Alerts */}
          <Card className="border-cyan-500/20 overflow-hidden">
            <CardHeader className="bg-gradient-to-br from-cyan-50/60 via-teal-50/30 to-transparent dark:from-cyan-950/20 dark:via-teal-950/10 dark:to-transparent border-b border-cyan-500/10">
              <div className="flex items-center justify-between gap-3">
                <CardTitle className="flex items-center gap-3 text-lg sm:text-xl">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-400 to-teal-500 text-white shadow-md shadow-cyan-500/30 flex-shrink-0">
                    <Bell className="h-4 w-4" />
                  </span>
                  <span className="bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                    Health Alerts for {destinationInfo.name}
                  </span>
                  {dateRange?.from && <span className="text-sm font-normal text-muted-foreground">
                      ({format(dateRange.from, "MMM yyyy")}{dateRange.to && ` - ${format(dateRange.to, "MMM yyyy")}`})
                    </span>}
                </CardTitle>
                {loadingAlerts && <LoadingSpinner size="sm" />}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {destinationInfo.currentAlerts.length > 0 ? destinationInfo.currentAlerts.map((alert, index) => <Alert key={index} className={getAlertColor(alert.severity)}>
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription className="font-medium">
                        <Badge variant="outline" className="mr-2">
                          {alert.severity.toUpperCase()}
                        </Badge>
                        {alert.message}
                        {alert.source && <div className="text-xs text-muted-foreground mt-1">
                            Source: {alert.source} • Updated: {alert.date && new Date(alert.date).toLocaleDateString()}
                          </div>}
                      </AlertDescription>
                    </Alert>) : <div className="flex justify-center py-8">
                    {loadingAlerts ? <LoadingSpinner size="md" text="Loading current health alerts..." /> : <p className="text-muted-foreground">No current health alerts for this location.</p>}
                  </div>}
              </div>
            </CardContent>
          </Card>

          {/* Emergency Medical Resources */}
          {destinationInfo.emergencyResources && <Card className="border-red-200 dark:border-red-800 bg-red-50/30 dark:bg-red-950/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-600 dark:text-red-400">
                  <Ambulance className="h-5 w-5" />
                  🚨 Emergency Medical Resources
                </CardTitle>
                <CardDescription className="text-red-700 dark:text-red-300">
                  Critical contacts and information for medical emergencies in {destinationInfo.name}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  
                  {/* Emergency Numbers */}
                  <div>
                    <h4 className="font-semibold text-sm uppercase tracking-wide text-red-600 dark:text-red-400 mb-3 flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      🚨 Emergency Numbers
                    </h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {destinationInfo.emergencyResources.emergencyNumbers.map((contact, index) => <a key={index} href={`tel:${contact.number}`} className="flex items-center gap-2 p-2.5 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-900/30 hover:border-red-300 dark:hover:border-red-700 transition-all group touch-manipulation">
                          <Phone className="h-3.5 w-3.5 text-red-600 dark:text-red-400 flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="font-medium text-red-900 dark:text-red-100 text-xs truncate">{contact.service}</p>
                            <p className="font-bold text-red-600 dark:text-red-400 text-sm">{contact.number}</p>
                          </div>
                        </a>)}
                    </div>
                  </div>

                  {/* Important Notice */}
                  <Alert className="bg-yellow-50 border-yellow-200">
                    <AlertTriangle className="h-4 w-4 text-yellow-600" />
                    <AlertDescription className="text-sm text-yellow-800">
                      <strong>Always call emergency services first.</strong> In life-threatening situations, dial emergency numbers immediately. Save these contacts offline or take screenshots in case of no internet access.
                    </AlertDescription>
                  </Alert>

                </div>
              </CardContent>
            </Card>}

          {/* Seasonal Health Information */}
          {destinationInfo.seasonalInfo && <Card className="border-cyan-500/20 overflow-hidden">
              <CardHeader className="bg-gradient-to-br from-cyan-50/60 via-teal-50/30 to-transparent dark:from-cyan-950/20 dark:via-teal-950/10 dark:to-transparent border-b border-cyan-500/10">
                <CardTitle className="flex items-center gap-3 text-lg sm:text-xl">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-400 to-teal-500 text-white shadow-md shadow-cyan-500/30 flex-shrink-0">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M12 3v18M9 12l3-3 3 3M12 3a9 9 0 0 1 9 9c0 2.5-1 4.75-2.5 6.5L12 21l-6.5-2.5C4 16.75 3 14.5 3 12a9 9 0 0 1 9-9z" />
                    </svg>
                  </span>
                  <span className="bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                    Seasonal Health Guide: {destinationInfo.seasonalInfo.season}
                  </span>
                  {dateRange?.from && <span className="text-sm font-normal text-muted-foreground">
                      ({format(dateRange.from, "MMMM yyyy")})
                    </span>}
                </CardTitle>
                <CardDescription>
                  Location-specific health risks and precautions for your travel period
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  
                  {/* Current Season Risks */}
                  <div>
                    <h4 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground mb-3">
                      {destinationInfo.seasonalInfo.season} Health Risks in {destinationInfo.name}
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                      {destinationInfo.seasonalInfo.risks.map((risk, index) => <div key={index} className="flex items-start gap-2 p-3 rounded-lg bg-orange-50 border border-orange-200">
                          <svg className="h-4 w-4 mt-0.5 text-orange-600 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path d="M12 9v4m0 4h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" />
                          </svg>
                          <span className="text-sm text-orange-800">{risk}</span>
                        </div>)}
                    </div>
                  </div>

                  {/* Seasonal Precautions */}
                  <div>
                    <h4 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground mb-3">
                      Recommended Precautions
                    </h4>
                    <div className="grid grid-cols-1 gap-2">
                      {destinationInfo.seasonalInfo.precautions.map((precaution, index) => <div key={index} className="flex items-start gap-2 p-3 rounded-lg bg-green-50 border border-green-200">
                          <svg className="h-4 w-4 mt-0.5 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path d="M9 12l2 2 4-4" />
                            <circle cx="12" cy="12" r="9" />
                          </svg>
                          <span className="text-sm text-green-800">{precaution}</span>
                        </div>)}
                    </div>
                  </div>

                  {/* Monthly Risk Calendar */}
                  {dateRange?.from && <div>
                      <h4 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground mb-3">
                        Health Risks During Your Travel Period
                      </h4>
                      <div className="space-y-2">
                        {(() => {
                    const monthsToShow = [];
                    const startMonth = dateRange.from!.getMonth();
                    const endMonth = dateRange.to ? dateRange.to.getMonth() : startMonth;
                    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                    if (endMonth >= startMonth) {
                      for (let i = startMonth; i <= endMonth; i++) {
                        monthsToShow.push(monthNames[i]);
                      }
                    } else {
                      for (let i = startMonth; i < 12; i++) {
                        monthsToShow.push(monthNames[i]);
                      }
                      for (let i = 0; i <= endMonth; i++) {
                        monthsToShow.push(monthNames[i]);
                      }
                    }
                    return monthsToShow.map((month, index) => {
                      const risks = destinationInfo.seasonalInfo?.monthlyRisks[month] || [];
                      return <div key={`${month}-${index}`} className="p-3 rounded-lg border bg-card">
                                <div className="font-medium text-sm text-primary mb-1">{month}</div>
                                <div className="flex flex-wrap gap-1">
                                  {risks.map((risk, riskIndex) => <span key={riskIndex} className="inline-block px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">
                                      {risk}
                                    </span>)}
                                </div>
                              </div>;
                    });
                  })()}
                      </div>
                    </div>}

                </div>
              </CardContent>
            </Card>}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            
            {/* Vaccinations & Preventive Care */}
            <Card className="border-cyan-500/20 overflow-hidden">
              <CardHeader className="bg-gradient-to-br from-cyan-50/60 via-teal-50/30 to-transparent dark:from-cyan-950/20 dark:via-teal-950/10 dark:to-transparent border-b border-cyan-500/10">
                <CardTitle className="flex items-center gap-3 text-lg">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-400 to-teal-500 text-white shadow-md shadow-cyan-500/30 flex-shrink-0">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                      <rect x="20" y="35" width="45" height="20" rx="10" strokeLinecap="round" strokeLinejoin="round" />
                      <line x1="65" y1="45" x2="85" y2="45" strokeLinecap="round" />
                      <rect x="10" y="40" width="15" height="10" rx="2" strokeLinecap="round" strokeLinejoin="round" />
                      <line x1="15" y1="40" x2="15" y2="25" strokeLinecap="round" />
                      <line x1="10" y1="25" x2="20" y2="25" strokeLinecap="round" />
                    </svg>
                  </span>
                  <span className="bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                    Travel Vaccinations
                  </span>
                </CardTitle>
                <CardDescription>
                  Consult with a travel medicine specialist 4-8 weeks before departure for personalized advice
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <Alert>
                    <Clock className="h-4 w-4" />
                    <AlertDescription>
                      <strong>Important Timing:</strong> Some vaccines require multiple doses over several weeks. 
                      Book your travel clinic appointment 6-8 weeks before departure to ensure adequate protection.
                    </AlertDescription>
                  </Alert>

                  {destinationInfo.vaccinations.filter(v => v.type === 'mandatory').length > 0 && <div>
                      <h4 className="font-semibold text-red-600 mb-2 flex items-center gap-1">
                        <AlertTriangle className="h-4 w-4" />
                        Mandatory (Required for Entry)
                      </h4>
                      <ul className="space-y-2 ml-4">
                        {destinationInfo.vaccinations.filter(v => v.type === 'mandatory').map((vaccine, index) => <li key={index} className="border-l-4 border-red-500 pl-3">
                            <div className="flex items-start gap-2">
                              <Shield className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                              <div>
                                <span className="font-medium text-sm">{vaccine.name}</span>
                                <div className="text-xs text-muted-foreground mt-1">
                                  <strong>Timing:</strong> {vaccine.timing}
                                </div>
                                {vaccine.description && <div className="text-xs text-muted-foreground mt-1">
                                    {vaccine.description}
                                  </div>}
                              </div>
                            </div>
                          </li>)}
                      </ul>
                    </div>}

                  {destinationInfo.vaccinations.filter(v => v.type === 'recommended').length > 0 && <div>
                      <h4 className="font-semibold text-orange-600 mb-2 flex items-center gap-1">
                        <Pill className="h-4 w-4" />
                        Recommended
                      </h4>
                      <ul className="space-y-1 ml-4 mb-3">
                        {destinationInfo.vaccinations.filter(v => v.type === 'recommended').map((vaccine, index) => <li key={index} className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-orange-500 rounded-full" />
                            <span className="text-sm font-medium">{vaccine.name}</span>
                          </li>)}
                      </ul>
                      <Dialog open={showVaccinationDetails} onOpenChange={setShowVaccinationDetails}>
                        <DialogTrigger asChild>
                          <Button variant="outline" size="sm" className="w-full h-11 touch-manipulation">
                            Show detailed vaccination info
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl max-h-[85vh] sm:max-h-[80vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle className="text-lg sm:text-xl">Complete Vaccination Information for {destinationInfo.name}</DialogTitle>
                            <DialogDescription>
                              Detailed vaccination requirements and recommendations
                            </DialogDescription>
                          </DialogHeader>
                          <div className="space-y-6">
                            {destinationInfo.vaccinations.filter(v => v.type === 'mandatory').length > 0 && <div>
                                <h4 className="font-semibold text-red-600 mb-2 flex items-center gap-1">
                                  <Shield className="h-4 w-4" />
                                  Mandatory
                                </h4>
                                <ul className="space-y-2 ml-4">
                                   {destinationInfo.vaccinations.filter(v => v.type === 'mandatory').map((vaccine, index) => <li key={index} className="border-l-4 border-red-400 pl-3">
                                      <div className="flex items-start gap-2">
                                        <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                                        <div>
                                          <span className="font-medium text-sm">{vaccine.name}</span>
                                          <div className="text-xs text-muted-foreground mt-1">
                                            <strong>Timing:</strong> {vaccine.timing}
                                          </div>
                                          {vaccine.description && <div className="text-xs text-muted-foreground mt-1">
                                              {vaccine.description}
                                            </div>}
                                          {vaccine.name.toLowerCase().includes('yellow fever') && <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                                              <a href="https://www.who.int/health-topics/yellow-fever" target="_blank" rel="noopener noreferrer" className="text-yellow-700 hover:text-yellow-900 underline font-medium">
                                                Check countries with risk of Yellow Fever transmission (WHO)
                                              </a>
                                            </div>}
                                        </div>
                                      </div>
                                    </li>)}
                                </ul>
                              </div>}

                            {destinationInfo.vaccinations.filter(v => v.type === 'recommended').length > 0 && <div>
                                <h4 className="font-semibold text-orange-600 mb-2 flex items-center gap-1">
                                  <Pill className="h-4 w-4" />
                                  Recommended
                                </h4>
                                <ul className="space-y-2 ml-4">
                                  {destinationInfo.vaccinations.filter(v => v.type === 'recommended').map((vaccine, index) => <li key={index} className="border-l-4 border-orange-400 pl-3">
                                      <div className="flex items-start gap-2">
                                        <Check className="h-4 w-4 text-orange-500 mt-0.5 flex-shrink-0" />
                                        <div>
                                          <span className="font-medium text-sm">{vaccine.name}</span>
                                          <div className="text-xs text-muted-foreground mt-1">
                                            <strong>Timing:</strong> {vaccine.timing}
                                          </div>
                                          {vaccine.description && <div className="text-xs text-muted-foreground mt-1">
                                              {vaccine.description}
                                            </div>}
                                        </div>
                                      </div>
                                    </li>)}
                                </ul>
                              </div>}

                            {destinationInfo.vaccinations.filter(v => v.type === 'routine').length > 0 && <div>
                                <h4 className="font-semibold text-cyan-600 mb-2 flex items-center gap-1">
                                  <Check className="h-4 w-4" />
                                  Routine Updates
                                </h4>
                                <ul className="space-y-2 ml-4">
                                  {destinationInfo.vaccinations.filter(v => v.type === 'routine').map((vaccine, index) => <li key={index} className="border-l-4 border-blue-400 pl-3">
                                      <div className="flex items-start gap-2">
                                        <Heart className="h-4 w-4 text-cyan-500 mt-0.5 flex-shrink-0" />
                                        <div>
                                          <span className="font-medium text-sm">{vaccine.name}</span>
                                          <div className="text-xs text-muted-foreground mt-1">
                                            <strong>Timing:</strong> {vaccine.timing}
                                          </div>
                                          {vaccine.description && <div className="text-xs text-muted-foreground mt-1">
                                              {vaccine.description}
                                            </div>}
                                        </div>
                                      </div>
                                    </li>)}
                                </ul>
                              </div>}

                            <div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                              <p className="text-xs text-blue-800 dark:text-blue-200">
                                💡 <strong>Pro Tip:</strong> Bring your vaccination records and consider getting a International Certificate of Vaccination 
                                (Yellow Card) for travel to high-risk areas. Some countries may require proof of vaccination at entry.
                              </p>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>}

                </div>
              </CardContent>
            </Card>

            {/* Travel Pharmacy Kit */}
            <Card className="border-cyan-500/20 overflow-hidden">
              <CardHeader className="bg-gradient-to-br from-cyan-50/60 via-teal-50/30 to-transparent dark:from-cyan-950/20 dark:via-teal-950/10 dark:to-transparent border-b border-cyan-500/10">
                <CardTitle className="flex items-center gap-3 text-lg">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-400 to-teal-500 text-white shadow-md shadow-cyan-500/30 flex-shrink-0">
                    <Pill className="h-4 w-4" />
                  </span>
                  <span className="bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                    Travel Pharmacy Essentials for {destinationInfo.name}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <ul className="space-y-2">
                  {destinationInfo.pharmacyItems.map((item, index) => <li key={index} className="flex items-center gap-2 p-2 rounded-lg hover:bg-cyan-50/50 dark:hover:bg-cyan-950/20 transition-colors">
                      <div className="w-1.5 h-1.5 bg-gradient-to-br from-cyan-400 to-teal-500 rounded-full flex-shrink-0" />
                      <span className="text-sm">{item}</span>
                    </li>)}
                </ul>
              </CardContent>
            </Card>

            {/* Nutrition Guidelines */}
            <Card className="border-cyan-500/20 overflow-hidden">
              <CardHeader className="bg-gradient-to-br from-cyan-50/60 via-teal-50/30 to-transparent dark:from-cyan-950/20 dark:via-teal-950/10 dark:to-transparent border-b border-cyan-500/10">
                <CardTitle className="flex items-center gap-3 text-lg">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-400 to-teal-500 text-white shadow-md shadow-cyan-500/30 flex-shrink-0">
                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="40" cy="50" r="35" fill="none" stroke="currentColor" strokeWidth="6" />
                      <rect x="48" y="40" width="3" height="17" fill="currentColor" />
                      <ellipse cx="49.5" cy="35" rx="4" ry="6" fill="currentColor" />
                    </svg>
                  </span>
                  <span className="bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                    Nutrition & Food Safety for {destinationInfo.name}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <ul className="space-y-2">
                  {destinationInfo.nutritionTips.map((tip, index) => <li key={index} className="flex items-center gap-2 p-2 rounded-lg hover:bg-cyan-50/50 dark:hover:bg-cyan-950/20 transition-colors">
                      <div className="w-1.5 h-1.5 bg-gradient-to-br from-cyan-400 to-teal-500 rounded-full flex-shrink-0" />
                      <span className="text-sm">{tip}</span>
                    </li>)}
                </ul>
              </CardContent>
            </Card>

            {/* Health Risks */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                  Health Risks in {destinationInfo.name}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Accordion type="multiple" className="w-full space-y-2">
                  {destinationInfo.healthRisks.map((risk, index) => {
                  if (typeof risk === 'string') {
                    return <AccordionItem key={index} value={`risk-${index}`} className="border-l-4 rounded-lg border">
                          <AccordionTrigger className="px-4 py-3 hover:no-underline text-left">
                            <div className="flex items-center gap-2 flex-1">
                              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                              <span className="font-medium text-sm line-clamp-1">{risk}</span>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="px-4 pb-4 pt-0">
                            <p className="text-sm text-muted-foreground leading-relaxed">{risk}</p>
                          </AccordionContent>
                        </AccordionItem>;
                  }
                  const severityColors = {
                    low: 'border-l-yellow-500 bg-yellow-50/50 dark:bg-yellow-950/20',
                    medium: 'border-l-orange-500 bg-orange-50/50 dark:bg-orange-950/20',
                    high: 'border-l-red-500 bg-red-50/50 dark:bg-red-950/20'
                  };
                  const severityBadgeColors = {
                    low: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
                    medium: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
                    high: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                  };
                  return <AccordionItem key={index} value={`risk-${index}`} className={cn("border-l-4 rounded-lg border", severityColors[risk.severity])}>
                        <AccordionTrigger className="px-4 py-3 hover:no-underline text-left">
                          <div className="flex items-center gap-2 flex-1">
                            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                            <span className="font-medium text-sm">{risk.name}</span>
                            <Badge variant="secondary" className={cn("text-xs ml-auto", severityBadgeColors[risk.severity])}>
                              {risk.severity}
                            </Badge>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-4 pb-4 pt-0">
                          <div className="space-y-3">
                            <p className="text-sm text-muted-foreground leading-relaxed">
                              {risk.description}
                            </p>
                            <div className="flex items-start gap-2 p-3 bg-background rounded-lg border">
                              <Shield className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                              <div>
                                <p className="text-xs font-semibold text-primary mb-1">Prevention</p>
                                <p className="text-xs text-foreground/80 leading-relaxed">
                                  {risk.prevention}
                                </p>
                              </div>
                            </div>
                          </div>
                        </AccordionContent>
                      </AccordionItem>;
                })}
                </Accordion>
              </CardContent>
            </Card>

          </div>

          {/* Medical Disclaimer */}
          <Alert className="border-amber-500 bg-amber-50 dark:bg-amber-950/20 mt-4">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            <AlertDescription className="text-sm">
              <div className="font-semibold text-amber-900 dark:text-amber-100 mb-2">Important Medical Disclaimer</div>
              <div className="text-amber-800 dark:text-amber-200 space-y-1">
                <p>This information is for <strong>general travel guidance only</strong> and should not replace professional medical advice.</p>
                <ul className="list-disc list-inside mt-2 space-y-1 text-xs">
                  <li>Always consult with a healthcare professional or travel medicine clinic before traveling</li>
                  <li>Health situations change rapidly - verify all requirements with official sources (WHO, CDC, embassy)</li>
                  <li>This system may not capture breaking outbreaks or recent health developments</li>
                  <li>Individual health needs vary - personalized medical assessment is essential</li>
                </ul>
              </div>
            </AlertDescription>
          </Alert>
        </div>}

      {/* Jet Lag Minimizer */}
      <Card className="border-cyan-500/20 overflow-hidden">
        <div className="relative bg-gradient-to-br from-cyan-50 via-teal-50/50 to-transparent dark:from-cyan-950/30 dark:via-teal-950/20 dark:to-transparent border-b border-cyan-500/10">
          <div className="absolute -top-12 -right-12 h-40 w-40 rounded-full bg-gradient-to-br from-cyan-400/20 to-teal-500/20 blur-3xl pointer-events-none" aria-hidden="true" />
          <CardHeader className="relative">
            <CardTitle className="flex items-center gap-3 text-xl sm:text-2xl">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-400 to-teal-500 text-white shadow-md shadow-cyan-500/30">
                <Clock className="h-5 w-5" />
              </span>
              <span className="bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                Jet Lag Minimizer
              </span>
            </CardTitle>
            <CardDescription className="text-sm sm:text-base">
              Plan your travel schedule to minimize jet lag effects
            </CardDescription>
          </CardHeader>
        </div>
        <CardContent>
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                <Button variant={showJetLagPlan ? "default" : "outline"} onClick={() => setShowJetLagPlan(!showJetLagPlan)} className="w-full sm:w-auto">
                  {showJetLagPlan ? 'Hide Plan' : 'Create Your Jet Lag Plan'}
                </Button>
                <Button 
                  variant={showFlightComparison ? "default" : "outline"} 
                  onClick={() => {
                    setShowFlightComparison(!showFlightComparison);
                    if (!showFlightComparison && flightOptions.length === 0) {
                      // Initialize with 2 empty flight options
                      setFlightOptions([
                        { id: '1', name: 'Morning Flight', departureTime: '08:00', arrivalTime: '14:00', flightDuration: 6 },
                        { id: '2', name: 'Evening Flight', departureTime: '20:00', arrivalTime: '10:00', flightDuration: 14 }
                      ]);
                    }
                  }}
                  className="w-full sm:w-auto"
                >
                  <BarChart3 className="h-4 w-4 mr-2" />
                  Compare Flight Options
                </Button>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {savedPlans.length > 0 && <>
                    <Button variant="outline" size="sm" onClick={() => setShowSavedPlans(!showSavedPlans)} className="flex-1 sm:flex-none">
                      <BookOpen className="h-4 w-4 mr-2" />
                      Saved Plans ({savedPlans.length})
                    </Button>
                    {comparisonPlans.length > 0 && <Button variant="outline" size="sm" onClick={() => setShowComparison(!showComparison)} className="flex-1 sm:flex-none">
                        <BarChart3 className="h-4 w-4 mr-2" />
                        Compare ({comparisonPlans.length})
                      </Button>}
                  </>}
                {showJetLagPlan && jetLagPlan.timeDifference !== 0 && <Button variant="default" size="sm" onClick={savePlan} disabled={savingPlan} className="flex-1 sm:flex-none">
                    <Save className="h-4 w-4 mr-2" />
                    {savingPlan ? 'Saving...' : 'Save Plan'}
                  </Button>}
              </div>
            </div>
            
            {/* Saved Plans List */}
            {showSavedPlans && savedPlans.length > 0 && <div className="border rounded-lg p-4 bg-background/50 space-y-3">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-sm">Your Saved Plans</h4>
                  <Button variant="ghost" size="icon" onClick={() => setShowSavedPlans(false)} className="h-8 w-8">
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {savedPlans.map(plan => <Card key={plan.id} className="relative border-cyan-500/20 hover:border-cyan-500/40 hover:shadow-md hover:shadow-cyan-500/10 transition-all">
                      <CardContent className="p-4 space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <h5 className="font-semibold text-sm truncate">{plan.plan_name}</h5>
                            <p className="text-xs text-muted-foreground">{plan.destination_name || 'No destination'}</p>
                          </div>
                          <Button variant="ghost" size="icon" onClick={() => toggleFavorite(plan.id, plan.is_favorite)} className="h-7 w-7 flex-shrink-0">
                            <Star className={cn("h-4 w-4", plan.is_favorite && "fill-yellow-400 text-yellow-400")} />
                          </Button>
                        </div>
                        
                        <div className="flex items-center gap-2 text-xs">
                          <Badge variant="outline" className="text-xs">
                            {plan.jet_lag_severity}
                          </Badge>
                          <span className="text-muted-foreground">{plan.time_difference}h diff</span>
                          <span className="text-muted-foreground">~{plan.recovery_days}d</span>
                        </div>
                        
                        <div className="flex gap-2 mt-3">
                          <Button variant="outline" size="sm" onClick={() => loadPlan(plan)} className="flex-1 text-xs h-8">
                            Load
                          </Button>
                          <Button variant={comparisonPlans.find(p => p.id === plan.id) ? "default" : "outline"} size="sm" onClick={() => addToComparison(plan)} className="flex-1 text-xs h-8">
                            {comparisonPlans.find(p => p.id === plan.id) ? '✓ Added' : 'Compare'}
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => deletePlan(plan.id, plan.plan_name)} className="h-8 w-8">
                            <Trash2 className="h-3 w-3 text-destructive" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>)}
                </div>
              </div>}
            
            {/* Flight Options Comparison Tool */}
            {showFlightComparison && <div className="border rounded-lg p-4 sm:p-6 bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-950/20 dark:to-cyan-950/20 space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="font-semibold text-lg flex items-center gap-2">
                      <BarChart3 className="h-5 w-5 text-primary" />
                      Flight Options Comparison
                    </h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      Compare different flight times to minimize jet lag
                    </p>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={() => setShowFlightComparison(false)}
                    className="h-8 w-8"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                {/* Prerequisites Check */}
                {(!jetLagPlan.currentTimezone || !jetLagPlan.destinationTimezone) ? (
                  <Alert className="bg-yellow-50 dark:bg-yellow-950/30 border-yellow-200 dark:border-yellow-800">
                    <AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                    <AlertDescription>
                      Please set your current timezone and destination timezone above to compare flight options.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <>
                    {/* Flight Options Grid - Landscape Layout */}
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                      {flightOptions.map((flight, index) => {
                        // Calculate jet lag for this flight option
                        const timeDiff = calculateTimeDifference(jetLagPlan.currentTimezone, jetLagPlan.destinationTimezone);
                        const isEastward = timeDiff > 0;
                        
                        // Base recovery calculation: eastward is harder (1 day per timezone), westward easier (0.67 days per timezone)
                        const baseRecoveryDays = Math.ceil(Math.abs(timeDiff) * (isEastward ? 1 : 0.67));
                        
                        // Arrival time significantly impacts recovery
                        const arrivalHour = parseInt(flight.arrivalTime.split(':')[0]);
                        const arrivalMinute = parseInt(flight.arrivalTime.split(':')[1]);
                        const arrivalDecimal = arrivalHour + (arrivalMinute / 60);
                        
                        // Optimal arrival: 2 PM - 8 PM (14:00 - 20:00)
                        // This allows staying awake until local bedtime (crucial for adaptation)
                        const isOptimalArrival = arrivalDecimal >= 14 && arrivalDecimal <= 20;
                        
                        // Early morning (before 8 AM): harder adaptation, risk of sleeping too early
                        const isEarlyMorning = arrivalDecimal < 8;
                        
                        // Late night (after 10 PM): disrupts sleep schedule, harder adaptation
                        const isLateNight = arrivalDecimal >= 22 || arrivalDecimal < 4;
                        
                        // Adjust recovery time based on arrival
                        let adjustedRecoveryDays = baseRecoveryDays;
                        if (isOptimalArrival) {
                          adjustedRecoveryDays = Math.max(1, baseRecoveryDays - 1); // 1 day faster recovery
                        } else if (isEarlyMorning || isLateNight) {
                          adjustedRecoveryDays = baseRecoveryDays + 1; // 1 day slower recovery
                        }
                        
                        // Severity based on time difference and recovery
                        let severity: 'Minimal' | 'Mild' | 'Moderate' | 'Severe' = 'Minimal';
                        if (adjustedRecoveryDays <= 2) severity = 'Minimal';
                        else if (adjustedRecoveryDays <= 4) severity = 'Mild';
                        else if (adjustedRecoveryDays <= 6) severity = 'Moderate';
                        else severity = 'Severe';

                        // Generate specific advice based on arrival time and direction
                        const getArrivalAdvice = () => {
                          if (isOptimalArrival) {
                            return {
                              title: "Excellent timing for adaptation",
                              tips: [
                                "Stay awake until 9-10 PM local time (even if tired)",
                                "Get bright light exposure until sunset",
                                "Eat dinner at local time to sync your metabolism",
                                "Light exercise (walk) helps maintain alertness"
                              ],
                              color: "text-green-700 dark:text-green-300"
                            };
                          } else if (isEarlyMorning) {
                            return {
                              title: "Challenging arrival time",
                              tips: [
                                "Do NOT nap - stay awake until at least 8 PM local time",
                                "Get immediate bright sunlight exposure (crucial)",
                                "Have caffeine only before 2 PM local time",
                                "Plan engaging activities to stay alert all day"
                              ],
                              color: "text-orange-700 dark:text-orange-300"
                            };
                          } else if (isLateNight) {
                            return {
                              title: "Difficult adaptation window",
                              tips: [
                                "Sleep as soon as possible after arrival",
                                "Set alarm for normal local wake time (7-8 AM)",
                                "Avoid bright screens before sleep",
                                "Consider a different flight if possible - this timing makes jet lag worse"
                              ],
                              color: "text-red-700 dark:text-red-300"
                            };
                          } else {
                            return {
                              title: "Moderate adaptation challenge",
                              tips: [
                                "Stay awake until at least 9 PM local time",
                                "Get sunlight exposure in late afternoon",
                                "Light meal at local dinner time",
                                "Avoid heavy exercise close to bedtime"
                              ],
                              color: "text-cyan-700 dark:text-blue-300"
                            };
                          }
                        };

                        const arrivalAdvice = getArrivalAdvice();
                        
                        return (
                          <Card key={flight.id} className={cn(
                            "relative border-2 transition-all hover:shadow-lg col-span-1 lg:col-span-2 xl:col-span-1",
                            isOptimalArrival && "border-green-500 dark:border-green-600 bg-green-50/30 dark:bg-green-950/20",
                            isLateNight && "border-red-500 dark:border-red-600 bg-red-50/30 dark:bg-red-950/20"
                          )}>
                            <CardContent className="p-4 sm:p-6">
                              {/* Header Row */}
                              <div className="flex items-center justify-between gap-4 mb-4">
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                  <Input 
                                    value={flight.name}
                                    onChange={(e) => {
                                      const newOptions = [...flightOptions];
                                      newOptions[index].name = e.target.value;
                                      setFlightOptions(newOptions);
                                    }}
                                    className="font-semibold text-lg border-0 px-0 h-auto focus-visible:ring-0 max-w-[200px]"
                                    placeholder="Flight name"
                                  />
                                  {isOptimalArrival && (
                                    <Badge variant="outline" className="text-xs bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-400 border-green-300 dark:border-green-700 whitespace-nowrap">
                                      ⭐ Best for Recovery
                                    </Badge>
                                  )}
                                  {isLateNight && (
                                    <Badge variant="outline" className="text-xs bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400 border-red-300 dark:border-red-700 whitespace-nowrap">
                                      ⚠️ Harder Adaptation
                                    </Badge>
                                  )}
                                </div>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => setFlightOptions(flightOptions.filter(f => f.id !== flight.id))}
                                  className="h-8 w-8 flex-shrink-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>

                              {/* Landscape Layout - Flight Times in Row */}
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                                <div>
                                  <Label className="text-sm text-muted-foreground mb-1.5 block">Departure Time</Label>
                                  <div className="relative">
                                    <Input 
                                      type="time"
                                      value={flight.departureTime}
                                      onChange={(e) => {
                                        const newOptions = [...flightOptions];
                                        newOptions[index].departureTime = e.target.value;
                                        setFlightOptions(newOptions);
                                      }}
                                      className="h-11 text-base pl-3 pr-10"
                                    />
                                    <Clock className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                                  </div>
                                </div>
                                <div>
                                  <Label className="text-sm text-muted-foreground mb-1.5 block">Arrival Time (Local)</Label>
                                  <div className="relative">
                                    <Input 
                                      type="time"
                                      value={flight.arrivalTime}
                                      onChange={(e) => {
                                        const newOptions = [...flightOptions];
                                        newOptions[index].arrivalTime = e.target.value;
                                        setFlightOptions(newOptions);
                                      }}
                                      className="h-11 text-base pl-3 pr-10"
                                    />
                                    <Clock className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                                  </div>
                                </div>
                                <div>
                                  <Label className="text-sm text-muted-foreground mb-1.5 block">Flight Duration (hours)</Label>
                                  <Input 
                                    type="number"
                                    value={flight.flightDuration}
                                    onChange={(e) => {
                                      const newOptions = [...flightOptions];
                                      newOptions[index].flightDuration = parseFloat(e.target.value) || 0;
                                      setFlightOptions(newOptions);
                                    }}
                                    className="h-11 text-base"
                                    step="0.5"
                                    min="0"
                                  />
                                </div>
                              </div>

                              {/* Science-Based Action Plan */}
                              <div className="pt-4 border-t">
                                <h5 className={cn("text-sm font-semibold mb-2", arrivalAdvice.color)}>
                                  {arrivalAdvice.title}
                                </h5>
                                <ul className="space-y-1.5">
                                  {arrivalAdvice.tips.map((tip, tipIdx) => (
                                    <li key={tipIdx} className="flex items-start gap-2 text-sm">
                                      <span className="text-muted-foreground">•</span>
                                      <span className="text-foreground/80">{tip}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>

                    {/* Add Flight Option Button */}
                    <Button
                      variant="outline"
                      onClick={() => {
                        setFlightOptions([
                          ...flightOptions,
                          { 
                            id: Date.now().toString(), 
                            name: `Flight ${flightOptions.length + 1}`, 
                            departureTime: '12:00', 
                            arrivalTime: '18:00', 
                            flightDuration: 6 
                          }
                        ]);
                      }}
                      className="w-full"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Another Flight Option
                    </Button>

                    {/* Best Option Recommendation */}
                    {flightOptions.length > 1 && (
                      <Alert className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 border-2 border-green-300 dark:border-green-700">
                        <Award className="h-5 w-5 text-green-600 dark:text-green-400" />
                        <AlertDescription>
                          <div className="space-y-2">
                            <p className="font-semibold text-green-900 dark:text-green-100">Scientific Recommendation:</p>
                            <p className="text-sm text-green-800 dark:text-green-200">
                              {(() => {
                                const timeDiff = calculateTimeDifference(jetLagPlan.currentTimezone, jetLagPlan.destinationTimezone);
                                const isEastward = timeDiff > 0;
                                
                                // Find best flight based on arrival time
                                const rankedFlights = [...flightOptions].map(flight => {
                                  const arrivalHour = parseInt(flight.arrivalTime.split(':')[0]);
                                  const arrivalMinute = parseInt(flight.arrivalTime.split(':')[1]);
                                  const arrivalDecimal = arrivalHour + (arrivalMinute / 60);
                                  
                                  // Score based on optimal arrival window (2 PM - 8 PM = 14:00 - 20:00)
                                  let score = 0;
                                  if (arrivalDecimal >= 14 && arrivalDecimal <= 20) {
                                    score = 100; // Optimal
                                  } else if (arrivalDecimal >= 10 && arrivalDecimal < 14) {
                                    score = 70; // Good (late morning/early afternoon)
                                  } else if (arrivalDecimal >= 20 && arrivalDecimal <= 22) {
                                    score = 60; // Acceptable (early evening)
                                  } else if (arrivalDecimal >= 8 && arrivalDecimal < 10) {
                                    score = 50; // Challenging (early morning)
                                  } else {
                                    score = 20; // Poor (night/very early morning)
                                  }
                                  
                                  return { ...flight, score, arrivalDecimal };
                                }).sort((a, b) => b.score - a.score);
                                
                                const bestFlight = rankedFlights[0];
                                const worstFlight = rankedFlights[rankedFlights.length - 1];
                                
                                const baseRecovery = Math.ceil(Math.abs(timeDiff) * (isEastward ? 1 : 0.67));
                                const bestRecovery = bestFlight.score >= 100 ? Math.max(1, baseRecovery - 1) : baseRecovery;
                                const worstRecovery = worstFlight.score <= 50 ? baseRecovery + 1 : baseRecovery;
                                
                                if (bestFlight.score >= 100) {
                                  return `${bestFlight.name} (arriving ${bestFlight.arrivalTime}) is optimal. Afternoon/evening arrivals let you stay awake until local bedtime (9-10 PM), which is crucial for circadian rhythm adaptation. Expected recovery: ~${bestRecovery} days vs ~${worstRecovery} days for ${worstFlight.name}.`;
                                } else if (bestFlight.score >= 70) {
                                  return `${bestFlight.name} (arriving ${bestFlight.arrivalTime}) is your best option, though slightly earlier than ideal. Try to stay awake until at least 9 PM local time with bright light exposure and activity. Expected recovery: ~${bestRecovery} days.`;
                                } else {
                                  return `All options have challenging arrival times. If possible, look for flights arriving between 2 PM - 8 PM local time. ${isEastward ? 'Eastward travel' : 'Westward travel'} across ${Math.abs(timeDiff)} time zones typically requires ~${baseRecovery} days recovery, but poor arrival timing adds 1-2 days.`;
                                }
                              })()}
                            </p>
                          </div>
                        </AlertDescription>
                      </Alert>
                    )}
                  </>
                )}
              </div>}
            
            {/* Comparison View */}
            {showComparison && comparisonPlans.length > 0 && <div className="border rounded-lg p-4 bg-gradient-to-br from-primary/5 to-accent/5 space-y-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-primary" />
                    Plan Comparison
                  </h4>
                  <Button variant="ghost" size="icon" onClick={() => {
                  setShowComparison(false);
                  setComparisonPlans([]);
                }} className="h-8 w-8">
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {comparisonPlans.map((plan, index) => <Card key={plan.id} className="relative border-2">
                      <CardContent className="p-4 space-y-3">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <Badge variant="outline" className="mb-2">Plan {index + 1}</Badge>
                            <h5 className="font-bold text-sm mb-1">{plan.plan_name}</h5>
                            <p className="text-xs text-muted-foreground line-clamp-1">{plan.destination_name}</p>
                          </div>
                          {plan.is_favorite && <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />}
                        </div>
                        
                        <Separator />
                        
                        <div className="space-y-2 text-xs">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Jet Lag Level:</span>
                            <Badge variant={plan.jet_lag_severity === 'Minimal' ? 'outline' : plan.jet_lag_severity === 'Mild' ? 'secondary' : plan.jet_lag_severity === 'Moderate' ? 'default' : 'destructive'} className="text-xs">
                              {plan.jet_lag_severity}
                            </Badge>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Time Difference:</span>
                            <span className="font-medium">{Math.abs(plan.time_difference)}h {plan.time_difference > 0 ? 'ahead' : 'behind'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Recovery Time:</span>
                            <span className="font-medium">~{plan.recovery_days} days</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Flight Duration:</span>
                            <span className="font-medium">{plan.flight_duration}h</span>
                          </div>
                          {plan.estimated_arrival && <div className="flex justify-between">
                              <span className="text-muted-foreground">Arrival Time:</span>
                              <span className="font-medium">{plan.estimated_arrival}</span>
                            </div>}
                        </div>
                        
                        <div className="flex gap-2 pt-2">
                          <Button variant="outline" size="sm" onClick={() => loadPlan(plan)} className="flex-1 text-xs h-8">
                            Load Plan
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => setComparisonPlans(comparisonPlans.filter(p => p.id !== plan.id))} className="h-8 w-8">
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>)}
                </div>
                
                {comparisonPlans.length > 1 && <div className="p-4 bg-primary/10 rounded-lg border border-primary/20">
                    <h5 className="font-semibold text-sm mb-3 flex items-center gap-2">
                      <Lightbulb className="h-4 w-4 text-primary" />
                      Recommendation
                    </h5>
                    <div className="text-sm space-y-2">
                      {(() => {
                    const bestPlan = [...comparisonPlans].sort((a, b) => {
                      const severityOrder = {
                        'Minimal': 1,
                        'Mild': 2,
                        'Moderate': 3,
                        'Severe': 4
                      };
                      const aScore = (severityOrder[a.jet_lag_severity as keyof typeof severityOrder] || 5) + a.recovery_days * 0.1;
                      const bScore = (severityOrder[b.jet_lag_severity as keyof typeof severityOrder] || 5) + b.recovery_days * 0.1;
                      return aScore - bScore;
                    })[0];
                    return <p className="text-foreground/80">
                            <strong className="text-primary">{bestPlan.plan_name}</strong> offers the best combination of lower jet lag severity and faster recovery time{bestPlan.estimated_arrival && ` with an optimal ${bestPlan.estimated_arrival} arrival`}.
                          </p>;
                  })()}
                    </div>
                  </div>}
              </div>}
            
            
            {showJetLagPlan && <div className="space-y-4 p-4 border border-cyan-500/20 rounded-xl relative overflow-hidden bg-gradient-to-br from-cyan-50/40 via-teal-50/20 to-transparent dark:from-cyan-950/20 dark:via-teal-950/10 dark:to-transparent dark:bg-cover dark:bg-center dark:border-blue-800/50" style={{ backgroundImage: `var(--jet-lag-bg)` }}>
                <style>{`:root { --jet-lag-bg: none; } .dark { --jet-lag-bg: url(${jetLagBg}); }`}</style>
                <div className="absolute inset-0 dark:bg-background/70"></div>
                <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Current Timezone */}
                  <div className="space-y-2 relative z-10">
                    <Label className="text-sm font-medium flex items-center gap-2 dark:text-foreground">
                      Your Current Timezone
                      {jetLagPlan.currentTimezone && <Badge variant="secondary" className="text-xs">Auto-detected</Badge>}
                    </Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left">
                          {jetLagPlan.currentTimezone || "Select timezone"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-80 p-0">
                        <Command>
                          <CommandList>
                            <CommandEmpty>No timezone found.</CommandEmpty>
                            <CommandGroup>
                              {getTimezonesList().map(tz => <CommandItem key={tz} value={tz} onSelect={() => {
                              setJetLagPlan(prev => ({
                                ...prev,
                                currentTimezone: tz,
                                timeDifference: prev.destinationTimezone ? calculateTimeDifference(tz, prev.destinationTimezone) : 0
                              }));
                            }}>
                                  {tz}
                                </CommandItem>)}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Destination Timezone */}
                  <div className="space-y-2 relative z-10">
                    <Label className="text-sm font-medium dark:text-foreground">Destination Timezone</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left">
                          {jetLagPlan.destinationTimezone || "Select timezone"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-80 p-0">
                        <Command>
                          <CommandList>
                            <CommandEmpty>No timezone found.</CommandEmpty>
                            <CommandGroup>
                              {getTimezonesList().map(tz => <CommandItem key={tz} value={tz} onSelect={async () => {
                              // Record usage for free tier users when they first use jet lag
                              if (subscription.subscription_tier === 'economy' && !jetLagUsageRecorded) {
                                const canUse = await recordUsage('jet_lag');
                                if (!canUse) {
                                  return; // Usage limit reached
                                }
                                setJetLagUsageRecorded(true);
                              }
                              setJetLagPlan(prev => ({
                                ...prev,
                                destinationTimezone: tz,
                                timeDifference: prev.currentTimezone ? calculateTimeDifference(prev.currentTimezone, tz) : 0
                              }));
                            }}>
                                  {tz}
                                </CommandItem>)}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Flight Duration */}
                  <div className="space-y-2 relative z-10">
                    <Label className="text-sm font-medium dark:text-foreground">Flight Duration (hours)</Label>
                    <Input type="number" placeholder="e.g., 8" value={jetLagPlan.flightDuration || ''} onChange={e => setJetLagPlan(prev => ({
                    ...prev,
                    flightDuration: parseFloat(e.target.value) || 0
                  }))} className="w-full" />
                  </div>

                  {/* Estimated Arrival Time */}
                  <div className="space-y-2 relative z-10">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                      <Label className="text-sm font-medium dark:text-foreground">
                        Estimated Time of Arrival (Local Time)
                      </Label>
                      <Badge variant="outline" className="text-[10px] sm:text-xs font-normal self-start sm:self-auto whitespace-nowrap">Optional - for personalized tips</Badge>
                    </div>
                    <Input type="time" placeholder="e.g., 14:30" value={jetLagPlan.estimatedArrival || ''} onChange={e => setJetLagPlan(prev => ({
                    ...prev,
                    estimatedArrival: e.target.value
                  }))} className="w-full" />
                    <p className="text-xs text-muted-foreground">
                      Enter arrival time to get personalized advice based on morning/afternoon/evening arrival
                    </p>
                  </div>
                </div>

                {/* Time Difference Display - Centered below */}
                {jetLagPlan.timeDifference !== 0 && <div className="flex justify-center mt-6 relative z-10">
                    <div className="w-full max-w-md">
                      <Label className="text-sm font-medium block text-center mb-2 dark:text-foreground">Time Difference</Label>
                      <div className={cn("p-3 rounded-lg text-center font-medium dark:border dark:border-border", getJetLagSeverity().bg, getJetLagSeverity().color)}>
                        {Math.abs(jetLagPlan.timeDifference)} hours {jetLagPlan.timeDifference > 0 ? 'ahead' : 'behind'}
                        <div className="text-xs font-normal mt-1">
                          Jet Lag Level: {getJetLagSeverity().level}
                        </div>
                      </div>
                    </div>
                  </div>}

                {/* Jet Lag Plan */}
                {jetLagPlan.timeDifference !== 0 && <div className="space-y-6 mt-6 relative z-10">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-full bg-primary">
                          <Clock className="h-5 w-5 text-primary-foreground" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-primary dark:text-foreground">
                            Your Jet Lag Recovery Plan
                          </h4>
                          <p className="text-sm text-muted-foreground">
                            {getJetLagSeverity().level} • ~{Math.ceil(Math.abs(jetLagPlan.timeDifference) * (jetLagPlan.timeDifference > 0 ? 1 : 0.67))}-day recovery
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-brand-yellow dark:text-brand-yellow">
                          {Math.abs(jetLagPlan.timeDifference)}h
                        </div>
                        <div className="text-xs text-muted-foreground">time diff</div>
                      </div>
                    </div>
                    
                    {/* Timeline Layout - Compact Grid for Mobile */}
                    <div className="relative">
                      <div className="hidden sm:block absolute left-4 top-0 bottom-0 w-0.5 bg-primary dark:bg-primary/50"></div>
                      
                      <div className="grid grid-cols-1 sm:space-y-6 gap-3 sm:gap-0">
                        {getJetLagAdvice().map((phase, index) => <div key={index} className="relative sm:pl-12">
                            <div className="hidden sm:flex absolute left-0 w-8 h-8 rounded-full bg-background dark:bg-background border-4 border-brand-yellow dark:border-brand-yellow items-center justify-center shadow-lg">
                              <span className="text-xs font-bold text-brand-yellow dark:text-brand-yellow">{index + 1}</span>
                            </div>
                            
                            <div className="bg-gradient-to-r from-background to-primary/10 dark:from-background/50 dark:to-primary/5 p-3 sm:p-4 rounded-xl border border-primary/20 dark:border-primary/30 shadow-sm">
                              <h5 className="font-semibold text-sm sm:text-base text-primary dark:text-foreground mb-2 flex items-center gap-2">
                                <span className="sm:hidden w-5 h-5 rounded-full bg-brand-yellow text-background text-xs flex items-center justify-center flex-shrink-0">{index + 1}</span>
                                {phase.phase}
                                <div className="flex-1 h-px bg-brand-yellow/50 dark:bg-brand-yellow/30"></div>
                              </h5>
                              
                              <div className="space-y-1.5 sm:space-y-2">
                                {phase.tips.map((tip, tipIndex) => <div key={tipIndex} className="flex items-start gap-2 text-xs sm:text-sm">
                                    <div className="w-1 h-1 rounded-full bg-brand-yellow dark:bg-brand-yellow mt-1.5 flex-shrink-0"></div>
                                    <span className="text-foreground/80 dark:text-foreground/90 leading-snug">{tip}</span>
                                  </div>)}
                              </div>
                            </div>
                          </div>)}
                      </div>
                    </div>
                    
                    {/* Compact Pro Tips */}
                    <div className="bg-brand-yellow-light/20 dark:bg-brand-yellow/10 border border-brand-yellow/30 dark:border-brand-yellow/40 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <div className="p-1.5 rounded-full bg-primary/20 dark:bg-primary/30">
                          <AlertTriangle className="h-4 w-4 text-primary dark:text-foreground" />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-primary dark:text-foreground mb-2">Science-Based Recovery Facts</p>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                            <div className="flex items-center gap-2 text-foreground/70 dark:text-foreground/80">
                              <div className="w-1 h-1 rounded-full bg-brand-yellow dark:bg-brand-yellow"></div>
                              Eastward: ~1 day/zone
                            </div>
                            <div className="flex items-center gap-2 text-foreground/70 dark:text-foreground/80">
                              <div className="w-1 h-1 rounded-full bg-brand-yellow dark:bg-brand-yellow"></div>
                              Westward: ~0.5-0.7 day/zone
                            </div>
                            <div className="flex items-center gap-2 text-foreground/70 dark:text-foreground/80">
                              <div className="w-1 h-1 rounded-full bg-brand-yellow dark:bg-brand-yellow"></div>
                              Light timing is key
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>}
              </div>}
          </div>
        </CardContent>
      </Card>


    </div>
    </>;
}